import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { createIzin, getIzinList, approveIzin, rejectIzin, getIzinDokumen } from '../../lib/api';
import { formatDate, getStatusColor, getStatusLabel } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { 
  ChevronLeft, Calendar as CalendarIcon, FileText, Plus,
  Check, X, Loader2, AlertCircle, Upload
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import { toast } from '../ui/sonner';

const IzinPage = ({ orgType }) => {
  const { user, isGuru, isKepalaSekolah, isAdminDinas, isPimpinan, isPengawas, isEditor, isPegawaiInstansi, isTenagaTataUsaha } = useAuth();
  const navigate = useNavigate();
  const [izinList, setIzinList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [tanggalMulai, setTanggalMulai] = useState(null);
  const [tanggalSelesai, setTanggalSelesai] = useState(null);
  const [alasan, setAlasan] = useState('');
  const [suratFile, setSuratFile] = useState(null);

  // Determine if this is a management view (sekolah/instansi) or personal view
  const isManagementView = !!orgType;
  
  // Roles that can submit izin (only on personal view, not management view)
  const canSubmitIzin = !isManagementView && (isGuru || isKepalaSekolah || isTenagaTataUsaha || isPegawaiInstansi);

  // Determine who can approve/reject in current view
  const canApproveInView = (izin) => {
    if (izin.status !== 'pending' || izin.guru_id === user?.id) return false;
    
    if (orgType === 'instansi') {
      // Only Pimpinan, Editor, Admin Dinas can approve instansi izin
      return isAdminDinas || isPimpinan || isEditor;
    }
    if (orgType === 'sekolah') {
      // Kepala sekolah can approve their school's guru/TU, admin/pimpinan/editor/pengawas can approve all
      return isKepalaSekolah || isAdminDinas || isPimpinan || isPengawas || isEditor;
    }
    // Default izin page (personal) - no approval buttons
    return false;
  };

  // Page title and description
  const pageTitle = orgType === 'sekolah' 
    ? 'Kelola Izin Sekolah' 
    : orgType === 'instansi' 
      ? 'Kelola Izin Instansi' 
      : canSubmitIzin ? 'Pengajuan Izin' : 'Kelola Izin';
  
  const pageDesc = orgType === 'sekolah'
    ? 'Kelola pengajuan izin dari guru dan staf sekolah'
    : orgType === 'instansi'
      ? 'Kelola pengajuan izin dari pegawai instansi'
      : canSubmitIzin ? 'Ajukan izin tidak hadir' : 'Setujui atau tolak pengajuan izin';

  useEffect(() => {
    loadIzin();
  }, [orgType]);

  const loadIzin = async () => {
    try {
      setLoading(true);
      const data = await getIzinList(orgType);
      setIzinList(data);
    } catch (error) {
      console.error('Failed to load izin:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!tanggalMulai || !tanggalSelesai || !alasan) {
      toast.error('Mohon lengkapi semua field');
      return;
    }

    setSubmitting(true);
    try {
      let suratBase64 = null;
      if (suratFile) {
        suratBase64 = await fileToBase64(suratFile);
      }

      await createIzin({
        tanggal_mulai: format(tanggalMulai, 'yyyy-MM-dd'),
        tanggal_selesai: format(tanggalSelesai, 'yyyy-MM-dd'),
        alasan,
        surat_base64: suratBase64
      });

      toast.success('Izin berhasil diajukan');
      setShowForm(false);
      resetForm();
      loadIzin();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengajukan izin');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (izinId) => {
    try {
      await approveIzin(izinId);
      toast.success('Izin disetujui');
      loadIzin();
    } catch (error) {
      toast.error('Gagal menyetujui izin');
    }
  };

  const handleReject = async (izinId) => {
    try {
      await rejectIzin(izinId);
      toast.success('Izin ditolak');
      loadIzin();
    } catch (error) {
      toast.error('Gagal menolak izin');
    }
  };

  const resetForm = () => {
    setTanggalMulai(null);
    setTanggalSelesai(null);
    setAlasan('');
    setSuratFile(null);
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
    });
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto" data-testid="izin-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-indigo-950" data-testid="izin-page-title">
              {pageTitle}
            </h1>
            <p className="text-gray-500">
              {pageDesc}
            </p>
          </div>
        </div>
        {canSubmitIzin && !showForm && (
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-indigo-950 hover:bg-indigo-900"
            data-testid="ajukan-izin-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajukan Izin
          </Button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <Card className="mb-6 border-indigo-100/50">
          <CardHeader>
            <CardTitle>Form Pengajuan Izin</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tanggal Mulai</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start border-indigo-200"
                        data-testid="tanggal-mulai-btn"
                      >
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {tanggalMulai ? format(tanggalMulai, 'dd MMM yyyy', { locale: id }) : 'Pilih tanggal'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={tanggalMulai}
                        onSelect={setTanggalMulai}
                        disabled={(date) => date < new Date()}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Tanggal Selesai</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start border-indigo-200"
                        data-testid="tanggal-selesai-btn"
                      >
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {tanggalSelesai ? format(tanggalSelesai, 'dd MMM yyyy', { locale: id }) : 'Pilih tanggal'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={tanggalSelesai}
                        onSelect={setTanggalSelesai}
                        disabled={(date) => date < (tanggalMulai || new Date())}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="alasan">Alasan</Label>
                <Textarea
                  id="alasan"
                  data-testid="alasan-input"
                  value={alasan}
                  onChange={(e) => setAlasan(e.target.value)}
                  placeholder="Jelaskan alasan izin Anda"
                  rows={3}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="surat">Surat Pendukung (Opsional)</Label>
                <div className="flex items-center gap-3">
                  <Input
                    id="surat"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setSuratFile(e.target.files[0])}
                    className="border-indigo-200"
                  />
                  {suratFile && (
                    <Badge variant="outline" className="whitespace-nowrap">
                      {suratFile.name}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="flex-1"
                >
                  Batal
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-indigo-950"
                  data-testid="submit-izin-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Mengirim...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Ajukan Izin
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Izin List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : izinList.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">Tidak ada data izin</p>
          <p className="text-sm">
            {canSubmitIzin ? 'Klik tombol "Ajukan Izin" untuk mengajukan izin baru' : 'Belum ada pengajuan izin'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {izinList.map((izin) => (
            <Card key={izin.id} className="border-indigo-100/50" data-testid={`izin-card-${izin.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    {isManagementView && (
                      <div className="mb-1">
                        <p className="font-semibold text-indigo-950">{izin.guru_nama}</p>
                        {orgType === 'sekolah' && izin.sekolah_nama && (
                          <p className="text-xs text-gray-500" data-testid={`izin-sekolah-nama-${izin.id}`}>
                            Sekolah: {izin.sekolah_nama}
                          </p>
                        )}
                        {orgType === 'instansi' && izin.instansi_nama && (
                          <p className="text-xs text-gray-500" data-testid={`izin-instansi-nama-${izin.id}`}>
                            Instansi: {izin.instansi_nama}
                          </p>
                        )}
                      </div>
                    )}
                    {!isManagementView && !canSubmitIzin && (
                      <p className="font-semibold text-indigo-950 mb-1">{izin.guru_nama}</p>
                    )}
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                      <CalendarIcon className="w-4 h-4" />
                      <span>{formatDate(izin.tanggal_mulai)} - {formatDate(izin.tanggal_selesai)}</span>
                    </div>
                    <p className="text-gray-700 mb-2">{izin.alasan}</p>
                    {izin.surat_url && (
                      <button
                        onClick={async () => {
                          try {
                            const result = await getIzinDokumen(izin.id);
                            if (result.surat_url) {
                              const base64Match = result.surat_url.match(/^data:(.+);base64,(.+)$/);
                              if (base64Match) {
                                const mimeType = base64Match[1];
                                const base64Data = base64Match[2];
                                const byteCharacters = atob(base64Data);
                                const byteArray = new Uint8Array(byteCharacters.length);
                                for (let i = 0; i < byteCharacters.length; i++) {
                                  byteArray[i] = byteCharacters.charCodeAt(i);
                                }
                                const blob = new Blob([byteArray], { type: mimeType });
                                const blobUrl = URL.createObjectURL(blob);
                                window.open(blobUrl, '_blank');
                              } else {
                                window.open(result.surat_url, '_blank');
                              }
                            }
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Gagal membuka dokumen');
                          }
                        }}
                        className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline"
                        data-testid={`lihat-surat-${izin.id}`}
                      >
                        <FileText className="w-4 h-4" />
                        Lihat Surat
                      </button>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge className={getStatusColor(izin.status)}>
                      {getStatusLabel(izin.status)}
                    </Badge>
                    
                    {canApproveInView(izin) && (
                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => handleApprove(izin.id)}
                          data-testid={`approve-izin-${izin.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Setujui
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-50"
                          onClick={() => handleReject(izin.id)}
                          data-testid={`reject-izin-${izin.id}`}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Tolak
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default IzinPage;
